import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { api } from '../utils/api';

// Get socket URL — connect directly to backend for Socket.IO
const getSocketUrl = () => {
  const backendUrl = (import.meta as any).env?.VITE_BACKEND_URL;
  if (backendUrl) {
    return backendUrl;
  }

  const apiUrl = (import.meta as any).env?.VITE_API_BASE_URL || '/api';

  // For absolute URL, remove /api suffix
  if (!apiUrl.startsWith('/')) {
    return apiUrl.replace(/\/api$/, '');
  }

  // Fallback: use current origin (only works if a reverse proxy handles /socket.io)
  return window.location.origin;
};

const SOCKET_URL = getSocketUrl();

export interface Message {
  id: number;
  job_id: number;
  sender_id: number;
  message_text: string;
  message_type: string;
  image_url?: string | null;
  read_at: string | null;
  created_at: string;
  updated_at: string;
  sender_name?: string;
  sender_email?: string;
  sender_role?: string;
}

interface UseSocketReturn {
  socket: Socket | null;
  isConnected: boolean;
  joinJob: (jobId: number) => void;
  leaveJob: (jobId: number) => void;
  sendMessage: (jobId: number, messageText: string, messageType?: string, imageUrl?: string) => void;
  onMessage: (callback: (message: Message) => void) => void;
  onMessageUpdated: (callback: (message: Message) => void) => void;
  onMessageDeleted: (callback: (data: { messageId: number }) => void) => void;
  onTyping: (callback: (data: { userId: number; jobId: number; isTyping: boolean }) => void) => void;
  onUserJoined: (callback: (data: { userId: number; jobId: number }) => void) => void;
  onUserLeft: (callback: (data: { userId: number; jobId: number }) => void) => void;
  onError: (callback: (error: { message: string }) => void) => void;
  emitTyping: (jobId: number, isTyping: boolean) => void;
}

export const useSocket = (): UseSocketReturn => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    let cancelled = false;

    const connect = async () => {
      let token: string;
      try {
        const res = await api.get<{ token: string }>('/auth/socket-token');
        token = res.data.token;
      } catch {
        return; // Not authenticated — skip socket connection
      }

      if (cancelled) return;

      // Determine if we should use secure connection based on the URL protocol
      const isSecure = typeof window !== 'undefined' &&
        (window.location.protocol === 'https:' || SOCKET_URL.startsWith('https://'));

      // Initialize socket connection
      console.log('🔌 Connecting to socket at:', SOCKET_URL);
      console.log('🔌 Protocol:', window.location.protocol);
      console.log('🔌 Using secure connection:', isSecure);
      const newSocket = io(SOCKET_URL, {
        path: '/socket.io/',
        auth: { token },
        extraHeaders: { Authorization: `Bearer ${token}` },
        transports: ['polling', 'websocket'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 10,
        reconnectionDelayMax: 5000,
        timeout: 20000,
        forceNew: false,
        upgrade: true,
        secure: isSecure,
        rejectUnauthorized: import.meta.env.PROD,
      });

      newSocket.on('connect', () => {
        console.log('✅ Socket connected');
        setIsConnected(true);
      });

      newSocket.on('disconnect', (reason) => {
        console.log('❌ Socket disconnected. Reason:', reason);
        setIsConnected(false);
      });

      newSocket.on('connect_error', (error) => {
        console.error('   Error message:', error.message);
        setIsConnected(false);
      });

      socketRef.current = newSocket;
      setSocket(newSocket);
    };

    connect();

    return () => {
      cancelled = true;
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
      }
    };
  }, []);

  const joinJob = useCallback((jobId: number) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('join-job', jobId);
    }
  }, [isConnected]);

  const leaveJob = useCallback((jobId: number) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('leave-job', jobId);
    }
  }, [isConnected]);

  const sendMessage = useCallback((jobId: number, messageText: string, messageType: string = 'text', imageUrl?: string) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('send-message', {
        jobId,
        messageText,
        messageType,
        imageUrl,
      });
    }
  }, [isConnected]);

  const onMessage = useCallback((callback: (message: Message) => void) => {
    if (socketRef.current) {
      socketRef.current.on('new-message', callback);
    }
  }, []);

  const onMessageUpdated = useCallback((callback: (message: Message) => void) => {
    if (socketRef.current) {
      socketRef.current.on('message-updated', callback);
    }
  }, []);

  const onMessageDeleted = useCallback((callback: (data: { messageId: number }) => void) => {
    if (socketRef.current) {
      socketRef.current.on('message-deleted', callback);
    }
  }, []);

  const onTyping = useCallback((callback: (data: { userId: number; jobId: number; isTyping: boolean }) => void) => {
    if (socketRef.current) {
      socketRef.current.on('typing', callback);
    }
  }, []);

  const onUserJoined = useCallback((callback: (data: { userId: number; jobId: number }) => void) => {
    if (socketRef.current) {
      socketRef.current.on('user-joined', callback);
    }
  }, []);

  const onUserLeft = useCallback((callback: (data: { userId: number; jobId: number }) => void) => {
    if (socketRef.current) {
      socketRef.current.on('user-left', callback);
    }
  }, []);

  const onError = useCallback((callback: (error: { message: string }) => void) => {
    if (socketRef.current) {
      socketRef.current.on('error', callback);
    }
  }, []);

  const emitTyping = useCallback((jobId: number, isTyping: boolean) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('typing', { jobId, isTyping });
    }
  }, [isConnected]);

  return {
    socket,
    isConnected,
    joinJob,
    leaveJob,
    sendMessage,
    onMessage,
    onMessageUpdated,
    onMessageDeleted,
    onTyping,
    onUserJoined,
    onUserLeft,
    onError,
    emitTyping,
  };
};
